import { type QueryClient, type QueryKey } from "@tanstack/react-query";

/** Cancels in-flight fetches, then applies `update` to the cached list. */
export const applyOptimisticList = async <T>(
	queryClient: QueryClient,
	queryKey: QueryKey,
	update: (items: T[]) => T[],
): Promise<T[]> => {
	await queryClient.cancelQueries({ queryKey });
	const previous = queryClient.getQueryData<T[]>(queryKey) ?? [];
	queryClient.setQueryData<T[]>(queryKey, (items) => update(items ?? []));
	return previous;
};

/**
 * Undoes one mutation's change without touching other items: each id is restored to its
 * pre-mutation value, re-inserted if it was removed, or dropped if it did not exist before.
 */
export const restoreById = <T extends { id: string }>(
	items: T[],
	previous: readonly T[],
	ids: readonly string[],
): T[] => {
	const idSet = new Set(ids);
	const previousById = new Map(previous.map((item) => [item.id, item]));
	const kept = items.flatMap((item) => {
		if (!idSet.has(item.id)) return [item];
		const before = previousById.get(item.id);
		return before ? [before] : [];
	});
	const keptIds = new Set(kept.map((item) => item.id));
	const reinserted = ids.flatMap((id) => {
		const before = previousById.get(id);
		return before && !keptIds.has(id) ? [before] : [];
	});
	return [...kept, ...reinserted];
};

export const replaceById = <T extends { id: string }>(items: T[], id: string, next: T): T[] =>
	items.map((item) => (item.id === id ? next : item));

/** Swaps in server copies of the items it knows about; leaves every other item (e.g. pending) alone. */
export const mergeById = <T extends { id: string }>(items: T[], updated: readonly T[]): T[] => {
	const updatedById = new Map(updated.map((item) => [item.id, item]));
	return items.map((item) => updatedById.get(item.id) ?? item);
};

export const patchById = <T extends { id: string }>(
	items: T[],
	id: string,
	patch: Partial<T>,
): T[] => items.map((item) => (item.id === id ? { ...item, ...patch } : item));
